import { useEffect, useRef, useState, type DragEvent } from "react";
import { Badge } from "@/components/ui/badge.js";
import { cn } from "@/lib/cn.js";
import { isoDate } from "@/lib/format.js";
import { durationMinutes } from "@/lib/format.js";
import type { Appointment, Customer, Resource, Service, SyncConflict, TenantClosure } from "@/lib/types.js";
import { AppointmentBlock } from "./appointment-block.js";
import { DAY_START_HOUR, MINUTES_PER_ROW, ROW_HEIGHT_PX, TOTAL_ROWS } from "./constants.js";
import { pickDayClosure, tenantDayLabel } from "./closures-overlay.js";

/**
 * Day view (extracted from the original `calendar.tsx`). Layout is unchanged
 * from REQ-033 / Fresha pattern: time gutter on the left, one resource column
 * per visible resource. PRD calendar-ux-parity REQ-CUX-002 mandates this view
 * preserves all prior behavior — drag-reschedule, today indicator, and the
 * closed-day overlay (REQ-CUX-012) — so the only change is the introduction
 * of the resize handle on each AppointmentBlock (REQ-CUX-006).
 */
export function CalendarDayGrid({
  day,
  tenantTz,
  resources,
  appointments,
  services,
  customers,
  conflicts,
  closures,
  onReschedule,
  onResize,
  onClickEmpty,
  onClickAppointment
}: {
  day: Date;
  tenantTz: string;
  resources: Resource[];
  appointments: Appointment[];
  services: Service[];
  customers: Customer[];
  conflicts: SyncConflict[];
  closures: TenantClosure[];
  onReschedule: (appointmentId: string, startsAt: string, resourceId: string) => void;
  onResize: (appointmentId: string, newEndsAt: string) => Promise<void>;
  onClickEmpty: (startsAt: string, resourceId: string) => void;
  onClickAppointment: (appointment: Appointment) => void;
}): React.JSX.Element {
  const visibleResources = resources.filter((r) => !r.deleted_at);
  const rows = Array.from({ length: TOTAL_ROWS }, (_, i) => i);
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const customerById = new Map(customers.map((c) => [c.id, c]));
  const isToday = isoDate(day) === isoDate(new Date());

  const [nowMinutes, setNowMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  useEffect(() => {
    if (!isToday) return;
    const id = window.setInterval(() => {
      const now = new Date();
      setNowMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60_000);
    return () => window.clearInterval(id);
  }, [isToday]);

  const nowRow = (nowMinutes - DAY_START_HOUR * 60) / MINUTES_PER_ROW;
  const showNow = isToday && nowRow >= 0 && nowRow <= TOTAL_ROWS;

  const dayLabel = tenantDayLabel(day, tenantTz);
  const dayClosure = pickDayClosure(closures, dayLabel, tenantTz);

  return (
    <div
      data-testid="calendar-day-grid"
      data-closed={dayClosure ? "true" : "false"}
      className="overflow-hidden rounded-lg border bg-card shadow-xs"
    >
      <div className="relative overflow-auto scrollbar-thin">
        {/* PRD §"Closed-day overlay": in day view, the entire grid background
            (gutter + headers + resource columns) is striped when the day is
            closed. The overlay is `pointer-events-none` so cells beneath
            stay interactive (booking dialogs, drag-drop). */}
        {dayClosure && (
          <div
            aria-hidden
            data-testid="day-grid-closure-stripe"
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(160, 160, 160, 0.18) 0, rgba(160, 160, 160, 0.18) 6px, transparent 6px, transparent 12px)"
            }}
          />
        )}
        <div
          className="grid"
          style={{
            gridTemplateColumns: `56px repeat(${visibleResources.length || 1}, minmax(180px, 1fr))`
          }}
        >
          {/* Header row */}
          <div className="sticky top-0 z-10 border-b bg-card/90 backdrop-blur" />
          {visibleResources.map((r) => (
            <div
              key={r.id}
              className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-l bg-card/90 px-3 py-2.5 backdrop-blur"
            >
              <span className="truncate text-sm font-semibold text-foreground">{r.name}</span>
              {r.freshness_state !== "fresh" && (
                <Badge variant="warning" className="capitalize">
                  {r.freshness_state}
                </Badge>
              )}
            </div>
          ))}

          {/* Time gutter */}
          <div className="relative border-r">
            {rows.map((row) => {
              const minutes = row * MINUTES_PER_ROW;
              const showLabel = minutes % 60 === 0;
              return (
                <div
                  key={row}
                  style={{ height: ROW_HEIGHT_PX }}
                  className={cn("relative", showLabel && "border-t")}
                >
                  {showLabel && (
                    <span className="absolute -top-2 right-2 bg-card px-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {formatHourLabel(DAY_START_HOUR + Math.floor(minutes / 60))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Resource columns */}
          {visibleResources.map((resource) => (
            <ResourceColumn
              key={resource.id}
              day={day}
              resource={resource}
              appointments={appointments.filter((a) => a.primary_resource_id === resource.id)}
              services={serviceById}
              customers={customerById}
              conflicts={conflicts}
              dayClosed={Boolean(dayClosure)}
              onReschedule={onReschedule}
              onResize={onResize}
              onClickEmpty={onClickEmpty}
              onClickAppointment={onClickAppointment}
            />
          ))}

          {showNow && <NowLine top={nowRow * ROW_HEIGHT_PX} columns={visibleResources.length || 1} />}
        </div>
      </div>
      {dayClosure && <ClosureBanner reason={dayClosure.reason} />}
    </div>
  );
}

function ClosureBanner({ reason }: { reason: string }): React.JSX.Element {
  return (
    <div className="border-t bg-warning/10 px-3 py-2 text-xs font-medium text-warning-foreground">
      <span className="inline-flex items-center gap-1.5 rounded-md bg-warning/30 px-2 py-0.5 font-semibold uppercase tracking-wider text-warning-foreground">
        Closed
      </span>{" "}
      <span>{reason}</span>
    </div>
  );
}

function NowLine({ top, columns }: { top: number; columns: number }): React.JSX.Element {
  return (
    <div
      className="pointer-events-none absolute z-[1] flex items-center"
      style={{ top, left: 0, right: 0, gridColumn: `1 / span ${columns + 1}`, transform: "translateY(-50%)" }}
    >
      <div className="-ml-1 h-2 w-2 rounded-full bg-destructive shadow-md" />
      <div className="h-px flex-1 bg-destructive/80" />
    </div>
  );
}

function ResourceColumn({
  day,
  resource,
  appointments,
  services,
  customers,
  conflicts,
  dayClosed,
  onReschedule,
  onResize,
  onClickEmpty,
  onClickAppointment
}: {
  day: Date;
  resource: Resource;
  appointments: Appointment[];
  services: Map<string, Service>;
  customers: Map<string, Customer>;
  conflicts: SyncConflict[];
  dayClosed: boolean;
  onReschedule: (appointmentId: string, startsAt: string, resourceId: string) => void;
  onResize: (appointmentId: string, newEndsAt: string) => Promise<void>;
  onClickEmpty: (startsAt: string, resourceId: string) => void;
  onClickAppointment: (a: Appointment) => void;
}): React.JSX.Element {
  const dayStart = new Date(day);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  const dragOverRef = useRef<HTMLDivElement | null>(null);

  function rowFromEvent(event: DragEvent<HTMLDivElement>): number {
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    return Math.max(0, Math.min(TOTAL_ROWS - 1, Math.floor(y / ROW_HEIGHT_PX)));
  }

  function isoForRow(row: number): string {
    const d = new Date(dayStart);
    d.setMinutes(d.getMinutes() + row * MINUTES_PER_ROW);
    return d.toISOString();
  }

  return (
    <div
      ref={dragOverRef}
      className={cn("relative border-l bg-background/40", dayClosed && "bg-muted/40")}
      style={{ height: TOTAL_ROWS * ROW_HEIGHT_PX }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData("application/x-appointment-id");
        if (!id) return;
        const row = rowFromEvent(e);
        onReschedule(id, isoForRow(row), resource.id);
      }}
    >
      {Array.from({ length: TOTAL_ROWS }, (_, i) => (
        <div
          key={i}
          className={cn(
            "absolute left-0 right-0 cursor-pointer transition-colors duration-100 hover:bg-primary/5 motion-reduce:transition-none",
            i % 4 === 0 && "border-t border-border",
            i % 4 === 2 && "border-t border-border/40"
          )}
          style={{ top: i * ROW_HEIGHT_PX, height: ROW_HEIGHT_PX }}
          onClick={() => onClickEmpty(isoForRow(i), resource.id)}
        />
      ))}
      {appointments.map((appointment) => {
        const top = minutesFromDayStart(appointment.starts_at, dayStart) / MINUTES_PER_ROW;
        const height = Math.max(2, durationMinutes(appointment.starts_at, appointment.ends_at) / MINUTES_PER_ROW);
        if (top < 0 || top > TOTAL_ROWS) return null;
        const service = services.get(appointment.service_id);
        const customer = customers.get(appointment.customer_id);
        return (
          <AppointmentBlock
            key={appointment.id}
            top={top * ROW_HEIGHT_PX}
            height={height * ROW_HEIGHT_PX}
            appointment={appointment}
            service={service}
            customer={customer}
            conflicts={conflicts}
            resizable
            onClick={() => onClickAppointment(appointment)}
            onDragStartReschedule={(event, id) => {
              event.dataTransfer.setData("application/x-appointment-id", id);
              event.dataTransfer.effectAllowed = "move";
            }}
            onResize={onResize}
          />
        );
      })}
    </div>
  );
}

function minutesFromDayStart(iso: string, dayStart: Date): number {
  return Math.floor((Date.parse(iso) - dayStart.getTime()) / 60000);
}

function formatHourLabel(hour: number): string {
  const am = hour < 12 || hour === 24;
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${am ? "a" : "p"}`;
}
